import { reactive, type Reactive } from 'vue'
import { defineStore } from 'pinia'
import type { Stageable } from '@/lib/entity/base'

type NoInfer<T> = [T][T extends any ? 0 : never]

export const useStageStore = defineStore('stage', () => {
  const store = reactive(new Map<string, Reactive<Stageable>>())

  const add = <T extends Reactive<Stageable>>(key: string, value: T): T => {
    store.set(key, value)
    return store.get(key) as T
  }

  const reset = (key: string) => store.get(key)?.reset()

  const clear = () => store.clear()

  const get = <T extends Stageable = never>(
    key: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...type_parameter_is_required: [T] extends [never] ? [invalid: never] : []
  ): NoInfer<Reactive<T> | undefined> => store.get(key) as Reactive<T> | undefined

  const find = <T extends Stageable = never>(
    predicate: (v: Reactive<Stageable>) => boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...type_parameter_is_required: [T] extends [never] ? [invalid: never] : []
  ): NoInfer<Reactive<T> | undefined> =>
    Array.from(store.values()).find(predicate) as Reactive<T> | undefined

  const has = (key: string) => store.has(key)

  const getAll = () => store

  const getModified = () => Array.from(store.values()).filter((e) => e.isModified())

  const getDeleted = () => Array.from(store.values()).filter((e) => e.isDeleted)

  const backupStage = () => {
    return Array.from(store.entries())
  }

  const restoreStage = (backup: Array<[string, Stageable]>) => {
    store.clear()
    backup.forEach(([key, value]) => {
      store.set(key, value)
    })
  }

  return {
    add,
    reset,
    clear,
    get,
    find,
    has,
    getAll,
    getModified,
    getDeleted,
    backupStage,
    restoreStage
  }
})

export type UseStageStoreReturnType = ReturnType<typeof useStageStore>
